import { registerApplication, start } from 'single-spa'
import {
  constructApplications,
  constructRoutes,
  constructLayoutEngine,
} from 'single-spa-layout'
import htmlTemplate from './serve-layout.html'
import { loader } from './loader.js'
import pkg from '../package.json'

// Добавление версии стилям для обхода кэша
// В исходники попадет только версия из package.json
const layout = htmlTemplate.replace(/<%=version%>/gm, pkg.version)
const routes = constructRoutes(layout)
const applications = constructApplications({
  routes,
  loadApp: ({ name }) => {
    return Promise.resolve()
      .then(() => {
        loader()
        if (!!~name.indexOf('@vite')) {
          // Если это приложение vite
          return import(/* webpackIgnore: true */ System.resolve(name))
        } else {
          return System.import(name)
        }
      })
      .then((app) => {
        return app
      })
  },
})

async function fetchLatestRelease() {
  try {
    const response = await fetch(
      'https://dev-api.letteros.com/release/latest',
      {
        method: 'GET',
        cache: 'no-store',
      }
    )
    return response.status === 200 ? await response.json() : null
  } catch (error) {
    console.error('Error fetching release:', error)
    return null
  }
}

function formatReleaseDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function getItemBadge(type) {
  if (type === 10) return { emoji: '🐞', text: 'Багфикс', color: '#ffebee' }
  if (type === 20) return { emoji: '✨', text: 'Фича', color: '#e8f5e9' }
  return { emoji: 'ℹ️', text: 'Инфо', color: '#e3f2fd' }
}

function createReleaseItem(item, index) {
  const badge = getItemBadge(item.type)
  const itemElement = document.createElement('div')
  itemElement.style.padding = '12px'
  itemElement.style.marginBottom = '8px'
  itemElement.style.borderRadius = '8px'
  itemElement.style.backgroundColor = badge.color
  itemElement.style.borderLeft = `4px solid ${badge.color
    .replace(')', ', 0.8)')
    .replace('rgb', 'rgba')}`
  itemElement.style.position = 'relative' // Для позиционирования стрелки

  // Создаем контейнер для заголовка
  const headerContainer = document.createElement('div')
  headerContainer.style.display = 'flex'
  headerContainer.style.justifyContent = 'space-between'
  headerContainer.style.alignItems = 'center'
  headerContainer.style.cursor = 'pointer'

  // Основное содержимое заголовка
  headerContainer.innerHTML = `
    <div style="display: flex; gap: 8px; align-items: center;">
      <span style="font-size: 18px;">${badge.emoji}</span>
      <span style="font-size: 12px; color: #666; min-width: max-content">${badge.text} — </span>
    </div>
    <div style="font-weight: 600; flex-grow: 1; margin: 0 6px;">${item.title}</div>
  `

  // Добавляем стрелочку
  const arrow = document.createElement('div')
  arrow.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" 
         style="transition: transform 0.2s ease; transform: rotate(0deg);">
      <path d="M19 9L12 16L5 9" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `
  headerContainer.appendChild(arrow)

  // Контейнер для описания (изначально скрыт)
  const descriptionContainer = document.createElement('div')
  descriptionContainer.style.fontSize = '14px'
  descriptionContainer.style.color = '#444'
  descriptionContainer.style.marginTop = '8px'
  descriptionContainer.style.paddingTop = '8px'
  descriptionContainer.style.borderTop = '1px solid rgba(0,0,0,0.1)'
  descriptionContainer.style.display = 'none' // Скрываем по умолчанию
  descriptionContainer.textContent = item.description

  // Добавляем элементы в основной контейнер
  itemElement.appendChild(headerContainer)
  itemElement.appendChild(descriptionContainer)

  // Обработчик клика для раскрытия/скрытия
  headerContainer.addEventListener('click', () => {
    const isHidden = descriptionContainer.style.display === 'none'
    descriptionContainer.style.display = isHidden ? 'block' : 'none'

    arrow.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)'

    // Плавная анимация
    if (isHidden) {
      descriptionContainer.style.animation = 'fadeIn 0.3s ease'
    }
  })

  return itemElement
}

async function showUpdateModal() {
  const lang = localStorage.getItem('lang') ?? 'ru'
  const release = await fetchLatestRelease()

  // Переводы
  const translate = {
    header: {
      en: 'New update available!',
      ru: 'Доступно новое обновление!',
    },
    text: {
      en: "We've improved the service and added new features. Refresh the page to access the latest version of the product",
      ru: 'Мы улучшили работу сервиса и добавили новые возможности. Обновите страницу, чтобы получить доступ к актуальной версии продукта',
    },
    button: {
      en: 'Update',
      ru: 'Обновить ',
    },
    showMore: {
      en: 'Show more',
      ru: 'Показать еще',
    },
    showLess: {
      en: 'Hide',
      ru: 'Скрыть',
    },
  }

  const modal = document.createElement('div')
  modal.style.position = 'fixed'
  modal.style.top = '0'
  modal.style.left = '0'
  modal.style.width = '100vw'
  modal.style.height = '100vh'
  modal.style.background = 'rgba(0, 0, 0, 0.5)'
  modal.style.display = 'flex'
  modal.style.alignItems = 'center'
  modal.style.justifyContent = 'center'
  modal.style.zIndex = '1000000000'
  modal.style.fontFamily = 'Raleway'

  const releaseContent = document.createElement('div')
  releaseContent.style.maxHeight = '70vh'

  if (release) {
    const releaseHeader = document.createElement('div')

    releaseHeader.innerHTML = `
      <h2 style="font-size: 18px; font-weight: 600;">
        ${release.title}
      </h2>
      <div style="font-size: 14px; color: #666; margin-bottom: 12px;">
        ${formatReleaseDate(release.releaseDate)}
      </div>
    `

    releaseContent.appendChild(releaseHeader)

    const itemsContainer = document.createElement('div')
    itemsContainer.style.overflowY = 'auto'
    itemsContainer.style.maxHeight = '60vh'
    itemsContainer.style.paddingRight = '8px'

    const style = document.createElement('style')
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: rgb(21, 21, 21, 0.4);
        border-radius: 4px;
      }
    `
    document.head.appendChild(style)

    itemsContainer.classList.add('custom-scrollbar')

    release.items.forEach((item, index) => {
      itemsContainer.appendChild(createReleaseItem(item, index))
    })

    const animation = document.createElement('style')
    animation.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; max-height: 0; }
        to { opacity: 1; max-height: 500px; }
      }
    `

    document.head.appendChild(animation)

    releaseContent.appendChild(itemsContainer)
  } else {
    const noReleaseInfo = document.createElement('div')
    noReleaseInfo.textContent = translate.text[lang]
    releaseContent.appendChild(noReleaseInfo)
  }

  const modalContent = document.createElement('div')
  modalContent.style.display = 'flex'
  modalContent.style.justifyContent = 'center'
  modalContent.style.alignItems = 'flex-start'
  modalContent.style.maxWidth = '800px'
  modalContent.style.width = '90%'

  const mainContent = document.createElement('div')
  mainContent.style.background = 'white'
  mainContent.style.borderRadius = '12px'
  mainContent.style.padding = '24px'
  mainContent.style.width = '100%'
  mainContent.style.maxWidth = '600px'
  mainContent.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'

  mainContent.innerHTML = `
    <h3 style="font-size: 20px; margin-bottom: 16px;">
      ${translate.header[lang]}
    </h3>
  `

  mainContent.appendChild(releaseContent)

  const refresh = document.createElement('div')
  refresh.style.marginTop = '24px'

  refresh.innerHTML = `
    <button id="spa-update-button" style="padding: 10px 16px; font-size: 14px; background-color: #001DFF; border-radius: 8px; color: #FFF; font-weight: 600; border: none; cursor: pointer;">
      ${translate.button[lang]}
    </button>
  `

  mainContent.appendChild(refresh)

  const closeButtonContainer = document.createElement('div')
  closeButtonContainer.classList.add('spa-cancel-button')
  closeButtonContainer.style.width = '40px'
  closeButtonContainer.style.height = '40px'
  closeButtonContainer.style.background = 'white'
  closeButtonContainer.style.borderRadius = '8px'
  closeButtonContainer.style.display = 'flex'
  closeButtonContainer.style.justifyContent = 'center'
  closeButtonContainer.style.alignItems = 'center'
  closeButtonContainer.style.marginLeft = '12px'
  closeButtonContainer.style.cursor = 'pointer'

  closeButtonContainer.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 5L5 15" stroke="#151515" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M5 5L15 15" stroke="#151515" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `

  modalContent.appendChild(mainContent)
  modalContent.appendChild(closeButtonContainer)
  modal.appendChild(modalContent)
  document.body.appendChild(modal)

  // Слушатель для кнопки обновления
  document.getElementById('spa-update-button').addEventListener('click', () => {
    window.location.reload(true)
  })

  // Слушатель для кнопки закрытия модального окна
  document.querySelector('.spa-cancel-button').addEventListener('click', () => {
    modal.remove()
  })
}

const layoutEngine = constructLayoutEngine({ routes, applications })
layoutEngine.version = pkg.version
layoutEngine.isActive()
applications.forEach(registerApplication)

window.addEventListener('popstate', (event) => {
  // eslint-disable-next-line no-console
  console.log('Fiered event "Popstate":', { ...event })
  fetch('/package.json', {
    method: 'GET',
    cache: 'no-store',
  })
    .then((response) => {
      return response.json()
    })
    .then((data) => {
      // eslint-disable-next-line no-console
      console.log('App current version: ', {
        currentVersion: data?.version,
        runnedVersion: pkg.version,
      })
      if (pkg.version && data.version && pkg.version != data.version) {
        showUpdateModal()
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.log('Error get current version: ', { ...err })
    })
    .finally(() => {
      // eslint-disable-next-line no-console
      console.log('Check release finalize')
    })
})

start()

